import React, { useState } from "react";
import Button from "../../Button";
import Chip, { ChipTone } from "../../Chip";
import Input from "../../Input";
import { ResearchRange, ResearchTable } from "../../../types/bd/Research";
import classes from "./research.module.scss";

/** A cell's one number ("AED 85,000" → 85000); null for none or a range. Mirrors crx-be BdRange.cellNumber. */
export const cellNumber = (v: unknown): number | null => {
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const found = String(v ?? "").match(/-?\d[\d,]*(?:\.\d+)?/g);
    return found && found.length === 1 ? Number(found[0].replace(/,/g, "")) : null;
};

/** Columns with at least 3 numbers: what a range check can use. */
export const numberColumns = (t: ResearchTable): string[] =>
    t.columns.filter((_c, i) => t.rows.filter((r) => cellNumber(r[i]) !== null).length >= 3);

const POSITION: Record<ResearchRange["position"], { tone: ChipTone; label: string }> = {
    below: { tone: "info", label: "Below the lower quartile" },
    inside: { tone: "success", label: "Inside the middle half" },
    above: { tone: "warning", label: "Above the upper quartile" },
};

/** "Owner's pay against 4 figures" — the range picture's title. */
export const rangeTitle = (r: ResearchRange) => `${r.label} against ${r.values.length} figures in “${r.column}”`;

/** Where the figure sits, as a chip; the figures are CRX's own, not the AI's. */
export const RangePosition: React.FC<{ range: ResearchRange }> = ({ range }) => (
    <Chip tone={POSITION[range.position].tone} icon="rulers" title="Worked out by CRX, not the AI, the same way as Excel's QUARTILE. The middle half runs from the lower to the upper quartile.">
        {POSITION[range.position].label}
    </Chip>
);

interface RangeFormProps {
    /** Keeps the fields' ids unique when several answers are open. */
    idKey: number;
    table: ResearchTable;
    range?: ResearchRange | null;
    onSave: (range: { column: string; value: string; label: string }) => Promise<boolean>;
    onCancel: () => void;
}

/** Pick the column and type the figure: CRX places it among the column's numbers. */
export const RangeForm: React.FC<RangeFormProps> = ({ idKey, table, range, onSave, onCancel }) => {
    const columns = numberColumns(table);
    const [form, setForm] = useState({ column: range?.column ?? columns[columns.length - 1] ?? "", value: range ? String(range.value) : "", label: range?.label ?? "" });
    const [isSaving, setIsSaving] = useState(false);
    // Input reports its id as the name: "rangeColumn-12" → column
    const FIELD: Record<string, keyof typeof form> = { rangeColumn: "column", rangeValue: "value", rangeLabel: "label" };
    const handleChange = (name: string, value: string | boolean | object) => setForm((f) => ({ ...f, [FIELD[name.split("-")[0]]]: String(value) }));
    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        const saved = await onSave(form);
        setIsSaving(false);
        if (saved) onCancel();
    };
    return (
        <form className={classes.rangeForm} onSubmit={handleSave}>
            <Input type="select" id={`rangeColumn-${idKey}`} label="Column to compare against" value={form.column} onChange={handleChange}
                options={columns.map((c) => ({ value: c, label: c }))} tooltip="Only columns with at least 3 numbers. Rows without a single number are left out." required />
            <Input type="text" id={`rangeValue-${idKey}`} label="Figure to check" value={form.value} onChange={handleChange}
                tooltip="One number, for example 950000 or AED 950,000. It stays in CRX: the AI never sees it." required />
            <Input type="text" id={`rangeLabel-${idKey}`} label="What it is (e.g. Owner's pay)" value={form.label} onChange={handleChange}
                tooltip="Shown on the picture and in the PDF." />
            <div className={classes.rangeActions}>
                <Button type="submit" size="sm" icon="rulers" disabled={isSaving || !form.column || !form.value.trim()} title="Work out where this figure sits among the column's numbers">{isSaving ? "Checking…" : "Check"}</Button>
                <Button type="button" size="sm" variant="ghost" onClick={onCancel} title="Close without checking">Cancel</Button>
            </div>
        </form>
    );
};
